import type { ToastAria } from '@react-aria/toast';
import type { ToastOptions as ReactAriaToastOptions } from '@react-stately/toast';

/**
 * Visual variants for styling a toast. These are used to convey the
 * message of the toast at the highest level.
 */
export enum ToastVariant {
  Success = 'success',
  Info = 'info',
  Error = 'error',
}

/**
 * Modified version of `ToastAria` from `@react-aria/toast` that replaces the
 * `onPress` handler with an `onClick` handler for the close button to support
 * the props contract for the Sprout implementation of a `Button`.
 */
export type ModifiedToastAria = Omit<ToastAria, 'toastProps' | 'closeButtonProps'> & {
  closeButtonProps: Omit<ToastAria['closeButtonProps'], 'onPress'> & {
    onClick: () => void;
  };
};

/**
 * Aria props used to render the contents of a toast. These are predominantly
 * provided by `@react-aria/toast`. We also supply a `closeToast` convenience
 * function to programmatically close the toast within the body of a custom
 * toast component.
 */
export type ToastRenderFnProps = ModifiedToastAria & {
  closeToast: () => void;
};

/**
 * A function that takes the aria props and returns a React node to render the
 * toast.
 */
export type ToastRenderFn = (props: ToastRenderFnProps) => React.ReactNode;

export type CustomToast = {
  key?: string;
  renderFn: ToastRenderFn;
  tags: Set<string>;
};

/**
 * Valid positions for a toast. Each must have an associated `ToastQueue` in
 * order to be used.
 */
export const TOAST_POSITIONS = ['top', 'bottom-left', 'bottom-center', 'bottom-right'] as const;

export type ToastPosition = (typeof TOAST_POSITIONS)[number];

/**
 * Valid contextual purposes for a toast, based on organizational system defined
 * here: https://coda.io/d/_d0cGUjr-ldg/Toast-notifications_su8qe_yE
 */
export const TOAST_PURPOSES = ['system', 'session', 'prompt', 'realtime'] as const;

/**
 * Contextual purpose for a toast, based on organizational system defined here:
 * https://coda.io/d/_d0cGUjr-ldg/Toast-notifications_su8qe_yE
 *
 * `system` - **System notification**: Critical system-level issues that require
 * immediate attention or may block the session from functioning entirely. These
 * are high-urgency, must-see messages for providers.
 *
 * `session` - **Session notification**: Session-related errors, guidance, or
 * success messages, meant to be easy-to-notice without overwhelming. Great for
 * feedback that relates to the current workflow or task.
 *
 * `prompt` - **Session prompt**: Lightweight, contextual actions or reminders
 * that are secondary in urgency or relevance. Great for passive or confirmatory
 * prompts that don’t need to block or interrupt the workflow.
 *
 * `realtime` - **Client and chat update**: Real-time, presence-related activity
 * related to clients and chat. Should be treated as more peripheral; best for
 * ongoing, non-blocking info.
 */
export type ToastPurpose = (typeof TOAST_PURPOSES)[number];

/**
 * Discriminated union options type for a toast position or purpose. A consumer
 * can supply either a `position` or a `purpose`, but not both at the same time.
 * If neither is supplied, the default position will be used.
 */
type ToastPositionOrPurpose =
  | {
      /**
       * Position on the screen where toast should appear. Prefer `purpose`
       * when possible to conform to information organization standards.
       */
      position: ToastPosition;
      purpose?: never;
    }
  | {
      /**
       * Contextual purpose for a toast, based on organizational system defined
       * here: https://coda.io/d/_d0cGUjr-ldg/Toast-notifications_su8qe_yE
       *
       * `system` - **System notification**: Critical system-level issues that
       * require immediate attention or may block the session from functioning
       * entirely. These are high-urgency, must-see messages for providers.
       *
       * `session` - **Session notification**: Session-related errors, guidance,
       * or success messages, meant to be easy-to-notice without overwhelming.
       * Great for feedback that relates to the current workflow or task.
       *
       * `prompt` - **Session prompt**: Lightweight, contextual actions or
       * reminders that are secondary in urgency or relevance. Great for passive
       * or confirmatory prompts that don’t need to block or interrupt the
       * workflow.
       *
       * `realtime` - **Client and chat update**: Real-time, presence-related
       * activity related to clients and chat. Should be treated as more
       * peripheral; best for ongoing, non-blocking info.
       */
      purpose: ToastPurpose;
      position?: never;
    }
  | { position?: never; purpose?: never };

/**
 * Options for enqueueing a new toast. Most of these are passed directly to
 * @react-aria/toast.
 */
export type ToastOptions = ReactAriaToastOptions &
  ToastPositionOrPurpose & {
    /**
     * Supply a unique key to ID the toast, e.g. if you need to programmatically
     * close it from a different part of the application.
     */
    key?: string;
    /**
     * Supply a set of tags to associate with the toast. This can be used to
     * programmatically bulk-close all toasts with a given tag, e.g. with
     * ```tsx
     * toast.closeByTag("my-tag");
     * // or more generally
     * toast.close("my-tag");
     * ```
     */
    tags?: Set<string>;
  };

/**
 * Options for enqueueing a new basic (i.e. non-custom) toast. Includes
 * additional UI options for the toast that would not be applicable to custom
 * toasts.
 */
export type BasicToastOptions = ToastOptions & {
  /** Whether the toast shows the dismiss button. Defaults to `true`. */
  isDismissible?: boolean;
  /** Whether the toast should be masked in Fullstory. Defaults to `false`. */
  isMasked?: boolean;
};
